'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { usePerm } from '@/hooks/use-perm';
import { isAuditTrailEnabled } from '@/lib/setupFeatures';
import { cn } from '@/lib/utils';

interface SettingsLayoutProps {
  children: React.ReactNode;
}

const settingsNav = [
  {
    title: 'Organization',
    href: 'organization',
  },
  {
    title: 'Access Tokens',
    href: 'access-tokens',
  },
  {
    title: 'Apple Pay',
    href: 'apple-pay',
  },
  {
    title: 'Demo',
    href: 'demo',
  },
  {
    title: 'Audit Trail',
    href: 'audit-trail',
  },
];

export default function SettingsLayout({ children }: SettingsLayoutProps) {
  const { t } = useTranslation('settings');
  const location = useLocation();
  const { orgRoutes, navigate } = useOrgNavigate();
  const { isAdmin, isLoaded } = usePerm();
  const auditTrailEnabled = isAuditTrailEnabled();

  // Get current active tab from URL
  const getCurrentTab = () => {
    if (location.pathname === orgRoutes.SETTINGS_ORGANIZATION()) return 'organization';
    if (location.pathname === orgRoutes.SETTINGS_ACCESS_TOKENS()) return 'access-tokens';
    if (location.pathname === orgRoutes.SETTINGS_APPLE_PAY()) return 'apple-pay';
    if (location.pathname === orgRoutes.SETTINGS_DEMO()) return 'demo';
    if (location.pathname === orgRoutes.SETTINGS_AUDIT_TRAIL()) return 'audit-trail';
    return 'organization'; // default
  };

  const handleTabChange = (value: string) => {
    switch (value) {
      case 'organization':
        navigate(orgRoutes.SETTINGS_ORGANIZATION());
        break;
      case 'access-tokens':
        navigate(orgRoutes.SETTINGS_ACCESS_TOKENS());
        break;
      case 'apple-pay':
        navigate(orgRoutes.SETTINGS_APPLE_PAY());
        break;
      case 'demo':
        navigate(orgRoutes.SETTINGS_DEMO());
        break;
      // Registered unconditionally, unlike the trigger below: `default` throws,
      // so a flag-off/flag-on mismatch would be a hard crash rather than a no-op.
      case 'audit-trail':
        navigate(orgRoutes.SETTINGS_AUDIT_TRAIL());
        break;
      default:
        throw new Error(`Invalid tab: ${value}`);
    }
  };

  // Admin-only guard
  if (isLoaded && !isAdmin) {
    return (
      <div className="bg-background flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="text-muted-foreground mx-auto h-12 w-12" />
          <h2 className="mt-4 text-xl font-semibold">{t('accessDenied')}</h2>
          <p className="text-muted-foreground mt-2 max-w-md">{t('adminOnlySection')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{t('title')}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">{t('description')}</p>
        </div>

        {/* Modern Tabs Navigation */}
        <Tabs value={getCurrentTab()} onValueChange={handleTabChange} className="w-full">
          <TabsList
            className={cn('mb-6 w-full sm:grid', auditTrailEnabled ? 'sm:grid-cols-5' : 'sm:grid-cols-4')}
          >
            <TabsTrigger value="organization">{t('tabs.organization')}</TabsTrigger>
            <TabsTrigger value="access-tokens">{t('tabs.accessTokens')}</TabsTrigger>
            <TabsTrigger value="apple-pay">{t('tabs.applePay')}</TabsTrigger>
            <TabsTrigger value="demo">{t('tabs.demo')}</TabsTrigger>
            {auditTrailEnabled && <TabsTrigger value="audit-trail">{t('tabs.auditTrail')}</TabsTrigger>}
          </TabsList>
        </Tabs>

        <div>{children}</div>
      </div>
    </div>
  );
}
